import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Radio, Steps, Divider, Col, Row } from 'antd';
import utils from '../../../utils';
import http from '../../../Http';
import SvgButton from "./SvgButton";

const MeterDirectionDetection = forwardRef((props, ref) => {
    const { t, i18n } = useTranslation();
    const setTip = useContext(utils.tipContext);

    const { sns } = {...props};

    const [selectedSn, setSelectedSn] = useState(null);
    const [step, setStep] = useState(0);
    const [detectingPercent, setDetectingPercent] = useState(0);  // Changed from null to 0
    const [startStep, setStartStep] = useState({
        title: "Start Detecting",
        description: "Request the inverter to start detecting",
    });
    const [detectingStep, setDetectingStep] = useState({
        title: "Detecting & Waiting",
        description: "Waiting for the detecting result"
    });
    const [endDetectingStep, setEndDetectingStep] = useState({
        title: "End Detecting",
        description: "Request the inverter to end detecting"
    });
    const [setCTDirection, setSetDirection] = useState({
        title: "Set Meter-Direction",
        description: "Set the inverter Meter-Direction according to the detection result"
    });
    const [loading, setLoading] = useState(false)
    const [modalVisible, setModalVisible] = useState(false);
    const [info, setInfo] = useState();
    const [meterDirection, setMeterDirection] = useState(null); //forward，reverse
    const [metterDirectionOld, setMeterDirectionOld] = useState(null);
    const [retryFromStepOne, setRetryFromStepOne] = useState(false);

    const refTimerForWaiting = useRef({StartTime:null, FirstRequestTime:null, LatestRequestTime:null, Timer: null});

    useEffect(() => {
        startStep.title = t("Start Detecting");
        startStep.description = t("Request the inverter to start detecting");
        detectingStep.title = t("Detecting & Waiting");
        detectingStep.description = t("Waiting for the detecting result");
        endDetectingStep.title = t("End Detecting");
        endDetectingStep.description = t("Request the inverter to end detecting");
        setCTDirection.title = t("Set Meter-Direction");
        setCTDirection.description = t("Set the inverter Meter-Direction according to the detection result");
    }, [t])

    useEffect(() => {
        if (sns.length > 0) {
            setSelectedSn(sns[0]);
        }
    }, [sns]);

    const handleOk = async () => {
        // const currentSelectedSn = refSelectedSn.current;
        // if (currentSelectedSn == 0) {
        //     return;
        // }
        console.log(selectedSn)
        setModalVisible(false);
    };

    const handleCancel = () => {
        if (step !== 1 && !loading) {
            setModalVisible(false);
        }
    };

    const handleStart = async() => {
        if (loading) {
            return;
        }
        setLoading(true);

        setInfo("");

        //取原始CT方向
        try {
            const resp = await http.postJSON("integrated-inverter-v2/setting/request", {
                SerialNumber: selectedSn,
                RW: "R",
                InverterSettingParams: [{
                    Address: 0x1406,
                    Count: 1,
                }],
            }).then(async (r) => {
                return await r.json();
            }).catch((e) => {
                throw e;
            });

            if (resp.code == 404) {
                setInfo(t("The inverter is offline. try again after the inverter comes online!"));
                return;
            } else if (resp.code >= 400) {
                setInfo(t("Server issue, please contact after-sales service!"));
                return;
            } else if (resp.code != 200) {
                setInfo("Error, try again later!");
                return;
            }

            if (resp.data.InverterSettingParams[0] && resp.data.InverterSettingParams[0].Result == 0) {
                setMeterDirectionOld(resp.data.InverterSettingParams[0].Data[1] == 1 ? "reverse" : "forward");
            } else {
                setInfo(t("Inverter getting parameter process failed, retry later or contact after-sales service!"))
            }

        } catch (e) {
            setInfo(t("Error, contact after-sales service!"));
            return;
        } finally {            
            setLoading(false);
        }

        //start
        try {
            const resp = await http.postJSON("integrated-inverter-v2/setting/request", {
                SerialNumber: selectedSn,
                RW: "W",
                InverterSettingParams: [{
                    Address: 0x4001,
                    Count: 1,
                    Data: [0, 1],
                }],
            }).then(async (r) => {
                return await r.json();
            }).catch((e) => {
                throw e;
            });

            if (resp.code == 404) {
                setInfo(t("The inverter is offline. try again after the inverter comes online!"));
                return;
            } else if (resp.code >= 400) {
                setInfo(t("Server issue, please contact after-sales service!"));
                return;
            } else if (resp.code != 200) {
                setInfo(t("Error, try again later!"));
                return;
            }

            if (resp.data.InverterSettingParams[0] && resp.data.InverterSettingParams[0].Result == 0) {
                detectingStep.subTitle = `Left ${utils.formatHHMMSS(20)}`;
                setDetectingStep({...detectingStep});
                setStep(step + 1);
                refTimerForWaiting.current.Timer = setInterval(handleTimerForWaiting, 1000);
                refTimerForWaiting.current.StartTime = new Date()
            } else {
                setInfo(t("Inverter startup detection process failed, retry later or contact after-sales service!"));
            }

        } catch (e) {
            setInfo(t("Error, contact after-sales service!"));
        } finally {
            setLoading(false);
        }
    }

    const handleTimerForWaiting = async () => {
        let tr = refTimerForWaiting.current
        if (!tr || !tr.Timer || !tr.StartTime) {
            return;
        }

        let st = tr.StartTime;
        let dt = new Date();
        let d = dt.getTime() - st.getTime();
        if (d < 21*1000) { //20秒内仅等待
            setDetectingPercent(d/1000/22*100);
            detectingStep.subTitle = `Left ${utils.formatHHMMSS(20 - d/1000)}`;
            setDetectingStep({...detectingStep});

            return;
        }

        if (tr.LatestRequestTime && dt.getTime() - tr.LatestRequestTime.getTime() < 3000) { 
            d = dt.getTime() - tr.FirstRequestTime.getTime();
            detectingStep.subTitle = `Overtime ${utils.formatHHMMSS(d/1000)}`;
            setDetectingStep({...detectingStep});
            return
        }
        
        try {
            const resp = await http.postJSON("integrated-inverter-v2/setting/request", {
                SerialNumber: selectedSn,
                RW: "R",
                InverterSettingParams: [{
                    Address: 0x1670,
                    Count: 8,
                    Data: [],
                }],
            }).then(async (r) => {
                return await r.json();
            }).catch((e) => {
                throw e;
            });

            if (resp.code == 404) {
                setInfo(t("The inverter is offline. try again after the inverter comes online!"));
                setRetryFromStepOne(true);
                clearInterval(tr.Timer)
                tr.Timer = null;
                tr.LatestRequestTime = null;
                tr.StartTime = null;
                delete detectingStep.subTitle;
                setDetectingStep({...detectingStep})
                setDetectingPercent(0);
                
                return;
            } else if (resp.code >= 400) {
                setInfo(t("Server issue, please contact after-sales service!"));
                setRetryFromStepOne(true);
                clearInterval(tr.Timer)
                tr.Timer = null;
                tr.LatestRequestTime = null;
                tr.StartTime = null;
                delete detectingStep.subTitle;
                setDetectingStep({...detectingStep})
                setDetectingPercent(0);

                return;
            } else if (resp.code != 200) {
                setInfo(t("Error, try again later!"));
                setRetryFromStepOne(true);
                clearInterval(tr.Timer)
                tr.Timer = null;
                tr.LatestRequestTime = null;
                tr.StartTime = null;
                delete detectingStep.subTitle;
                setDetectingStep({...detectingStep})
                setDetectingPercent(0);

                return;
            }

            if (resp.data.InverterSettingParams[0] && resp.data.InverterSettingParams[0].Result == 0) {
                const data = resp.data.InverterSettingParams[0].Data;
                let bReverse = false;
                for (let i=0; i< 8; i++) {
                    const code = (data[i]<<8) + data[i+1];
                    if (code == 63) {
                        bReverse = true;
                        break;
                    }
                }
                if (bReverse) {
                    setMeterDirection("reverse");
                    clearInterval(tr.Timer)
                    tr.Timer = null;
                    tr.LatestRequestTime = null;
                    tr.StartTime = null;
                    delete detectingStep.subTitle;
                    setDetectingStep({...detectingStep})
                    setDetectingPercent(0);
                    setStep(2);
                } else {
                    if (tr.LatestRequestTime) { //只有两次机会，然后就任务逆变器正接
                        setMeterDirection("forward");
                        clearInterval(tr.Timer)
                        tr.Timer = null;
                        tr.LatestRequestTime = null;
                        tr.StartTime = null;
                        delete detectingStep.subTitle;
                        setDetectingStep({...detectingStep})
                        setDetectingPercent(0);
                        setStep(2);
                    }
                }
            } else {
                setInfo(t("Inverter startup detection process failed, retry later or contact after-sales service!"));
                setRetryFromStepOne(true);
                clearInterval(tr.Timer)
                tr.Timer = null;
                tr.LatestRequestTime = null;
                tr.StartTime = null;
                delete detectingStep.subTitle;
                setDetectingStep({...detectingStep})
                setDetectingPercent(0);
            }

        } catch (e) {
            setInfo(t("Error, contact after-sales service!"));
            setRetryFromStepOne(true);
            clearInterval(tr.Timer)
            tr.Timer = null;
            tr.LatestRequestTime = null;
            tr.StartTime = null;
            delete detectingStep.subTitle;
            setDetectingStep({...detectingStep})
            setDetectingPercent(0);
        } finally {
            tr.LatestRequestTime = new Date();
            if (!tr.FirstRequestTime) {
                tr.FirstRequestTime = tr.LatestRequestTime;
            }
        }
    }

    const gotoStepOne = () => {
        setInfo("");
        setRetryFromStepOne(false);
        setStep(0);
    }

    const handleEnd = async() => {
        if (loading) {
            return;
        }
        setLoading(true);

        setInfo("");

        try {
            const resp = await http.postJSON("integrated-inverter-v2/setting/request", {
                SerialNumber: selectedSn,
                RW: "W",
                InverterSettingParams: [{
                    Address: 0x4001,
                    Count: 1,
                    Data: [0, 0],
                }],
            }).then(async (r) => {
                return await r.json();
            }).catch((e) => {
                throw e;
            });

            if (resp.code == 404) {
                setInfo(t("The inverter is offline. try again after the inverter comes online!"));
                return;
            } else if (resp.code >= 400) {
                setInfo(t("Server issue, please contact after-sales service!"));
                return;
            } else if (resp.code != 200) {
                setInfo(t("Error, try again later!"));
                return;
            }

            if (resp.data.InverterSettingParams[0] && resp.data.InverterSettingParams[0].Result == 0) {
                setStep(3);
            } else {
                setInfo(t("Inverter shutdown detection process failed, retry later or contact after-sales service!"));
            }

        } catch (e) {
            setInfo(t("Error, contact after-sales service!"));
        } finally {
            setLoading(false);
        }
    }

    const handleSetMeterDirection = async() => {
        if (loading) {
            return;
        }
        setLoading(true);

        setInfo("");

        try {
            const resp = await http.postJSON("integrated-inverter-v2/setting/request", {
                SerialNumber: selectedSn,
                RW: "W",
                InverterSettingParams: [{
                    Address: 0x1406,
                    Count: 1,
                    Data: [0, meterDirection=="reverse"?1:0],
                }],
            }).then(async (r) => {
                return await r.json();
            }).catch((e) => {
                throw e;
            });

            if (resp.code == 404) {
                setInfo(t("The inverter is offline. try again after the inverter comes online!"));
                return;
            } else if (resp.code >= 400) {
                setInfo(t("Server issue, please contact after-sales service!"));
                return;
            } else if (resp.code != 200) {
                setInfo("Error, try again later!");
                return;
            }

            if (resp.data.InverterSettingParams[0] && resp.data.InverterSettingParams[0].Result == 0) {
                setInfo(t("It's all done."));
                setStep(4);
            } else {
                setInfo(t("Inverter Setting process failed, retry later or contact after-sales service!"));
            }

        } catch (e) {
            setInfo(t("Error, contact after-sales service!"));
        } finally {
            setLoading(false);
        }
    }

    const doMeterDirectionDetection = () => {
        if (refTimerForWaiting.current && refTimerForWaiting.current.Timer) {
            clearInterval(refTimerForWaiting.current.Timer);
        }
        setInfo("");
        setDetectingPercent(0);
        setMeterDirection(null);
        setStep(0);
        setModalVisible(true);
        delete detectingStep.subTitle;
        setDetectingStep({...detectingStep});
        setMeterDirectionOld(null);
    };

    const handleAnotherRoute = () => {
        setInfo("");
        setRetryFromStepOne(false);
        setStep(0);
        setMeterDirection(null);
        setMeterDirectionOld(null);
    }

    useImperativeHandle(ref, () => ({
        doMeterDirectionDetection: doMeterDirectionDetection
    }));

    return ReactDOM.createPortal(<div onClick={(e)=>e.stopPropagation()}>
        <Modal
            open={modalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
            width="80%"
            style={{
                minWidth: "70%",
            }}
            maskClosable={false}
            title={<div>
                {t("Meter Direction Detection")}
            </div>}
            cancelButtonProps={{
                disabled: step == 1 || step == 4 || loading
            }}
            okButtonProps={{
                disabled: step != 4 || loading
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div disabled style={{ fontWeight: "bold" }}>{t("Please select an inverter")+":"}</div>
            <div style={{paddingBottom:"0"}}>
                <Radio.Group 
                    disabled={step != 0} 
                    options={sns} 
                    value={selectedSn} 
                    onChange={({ target: { value } }) => {
                        setSelectedSn(value)
                    }} 
                />
            </div>
            <Divider><span style={{color:"#aaa",fontSize:"0.8em"}}>{selectedSn??""}</span></Divider>
            <div>
                <Steps 
                    current={step} 
                    percent={detectingPercent || 0}  // Ensure percent is always a number
                    items={[startStep, detectingStep, endDetectingStep, setCTDirection]} 
                />
                <div style={{height:15}}></div>
                <Row>
                    <Col span={6} style={{display:"flex",flexDirection:"column"}}>
                        <div style={{
                            display: (step==0 ? "block" : "none"),
                        }}>
                            <Button type="primary" shape="round" disabled={!selectedSn || step != 0} onClick={handleStart} style={{
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                            }}>{t("Start")}</Button>
                            <div>
                                <span style={{color:"red",fontSize:"0.7em", paddingTop:"3px"}}>*{t("Note: After startup, the inverter will remain in a strong charging state until the \"End Detecting\" in step 3 is completed.")}</span>
                            </div>
                        </div>
                    </Col>
                    <Col span={6}>
                        <Button type="primary" shape="round" style={{display: (retryFromStepOne&&step==1) ? "block" : "none"}} onClick={gotoStepOne}>{t("Retry From Step 1")}</Button>
                    </Col>
                    <Col span={6}>
                        <Button type="primary" shape="round" disabled={!selectedSn || step != 2} onClick={handleEnd} style={{
                            display: step<=2 ? "block": "none",
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                        }}>{t("End")}</Button>
                    </Col>
                    <Col span={6}>
                        <Button type="primary" shape="round" disabled={!selectedSn || step != 3} onClick={handleSetMeterDirection} style={{
                            display: step==4 ? "none" : "block",
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                        }}>{t("Set Meter-Direction")} [{metterDirectionOld ? t(metterDirectionOld) : "--"} → {meterDirection ? t(meterDirection) : "--"}]</Button>
                        <div style={{
                            display: step==4 && (sns??[]).length>1 ? "flex" : "none",
                            flexDirection: "column"
                        }}>
                            {t("Please click Button OK to leave or")}:
                            <div>
                                <Button type="primary" shape="round" onClick={handleAnotherRoute} >{t("Detecting Other Inverter")}</Button>
                            </div>
                        </div>
                    </Col>
                </Row>
                {/* info */}
                <div style={{
                    display: info?"block":"none",
                    color: step < 4 ? "red" : "limegreen",
                    fontSize: step < 4 ? "14px" : "20px",
                    padding: "10px 5px",
                }}>
                    {info}
                </div>
            </div>

        </Modal></div>,
        document.body
    );
});

export default MeterDirectionDetection;